/**
 * @file OrderCreateHeader.tsx
 * @description Заголовок формы «Новый заказ» в верхней панели (phase19.24
 * шаг 5.1; phase19.31 — `CabinetPageHeader`, SC34).
 *
 * Главное действие одно — «Создать и отправить на согласование» (brand);
 * редкие исходы — черновик без письма и согласование по телефону — видимыми
 * кнопками-контурами (SC67, без «⋯») строкой под шапкой на любой ширине
 * (`secondaryBelow`): в панели рядом с главной они съедали название.
 * На телефоне главная кнопка уезжает в общую нижнюю полосу каркаса, а
 * причина запрета пишется над ней (`mobileNote`): серая кнопка без
 * объяснения читается как поломка.
 */

import { FileText, Phone, Send } from "lucide-react";

import type { OrderCreateSubmitMode } from "../../hooks/useOrderCreateForm";
import { Button } from "../ui/button";
import { PageActionButton } from "../ui/cabinet-page-header-parts";
import { CabinetPageHeader } from "../ui/CabinetPageHeader";
import type { PageAction } from "../ui/page-actions";

interface Props {
  /** Право `SHOP_MANAGE_ORDERS`: без него заказ не создать ни одним способом. */
  canManage: boolean;
  saving: boolean;
  /** Почему нельзя отправить письмо; `null` — можно. */
  blockedReason: string | null;
  /** Куда ведёт «назад» — реестр заказов. */
  backTo: string;
  onSubmit: (mode: OrderCreateSubmitMode) => void;
}

export function OrderCreateHeader({ canManage, saving, blockedReason, backTo, onSubmit }: Props) {
  // Нет прав — заблокированы ВСЕ способы, включая черновик: сервер ответит 403.
  const busy = saving || !canManage;
  const secondary: PageAction[] = [
    { key: "draft", label: "Черновик без письма", icon: FileText, disabled: busy, run: () => onSubmit("draft") },
    { key: "phone", label: "Согласовать по телефону", icon: Phone, disabled: busy, run: () => onSubmit("phone") },
  ];

  return (
    <CabinetPageHeader
      title="Новый заказ"
      subtitle="Соберите состав, выберите способ получения и отправьте условия клиенту"
      back={{ to: backTo, label: "К реестру заказов" }}
      secondaryActions={secondary.map((action) => <PageActionButton key={action.key} action={action} />)}
      // Три кнопки в панели на 1280 резали название до «Новый …» (qa4 п. 3):
      // редкие исходы — строкой под шапкой на любой ширине, в панели — главная.
      secondaryBelow
      mobileNote={blockedReason ? <span className="text-[var(--status-attention-fg,var(--warning))]">{blockedReason}</span> : undefined}
      action={
        <Button
          variant="brand"
          disabled={busy || !!blockedReason}
          loading={saving}
          title={blockedReason ?? undefined}
          onClick={() => onSubmit("send")}
          // Длинная подпись на 360 переносится, а не вылезает за экран.
          className="min-w-0 whitespace-normal text-center"
        >
          {saving ? null : <Send className="size-4" />}
          Создать и отправить на согласование
        </Button>
      }
    />
  );
}
